import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { AlertTriangle, BarChart3, Building2, CheckCircle2, ClipboardList, DollarSign, Download, FileSpreadsheet, FileText, Loader2, Tags, Users } from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { useOsReports } from '@/hooks/useOsReports';
import { api } from '@/lib/api';
import { EmptyState, ErrorState, KPICard } from '@mustauto/ui/web';
import { Card, CardHeader, CardTitle, CardContent } from '@mustauto/ui/web';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@mustauto/ui/web';
import { formatReais } from '@/lib/utils';
import { useToast } from '@mustauto/ui/web';
import { CHART_COLORS } from '@/lib/status';
import { addDaysIsoDate, formatApiError, formatDayMonthBR, todayIsoIn } from '@mustauto/utils';

type PeriodPreset = 'week' | 'month' | 'quarter';

/** O período é contado no fuso da oficina, não no do navegador nem em UTC.
 *
 *  `new Date().toISOString().slice(0, 10)` devolve o dia em UTC: depois das
 *  21h em Brasília o "hoje" já é amanhã, e o relatório passava a pedir um
 *  intervalo que terminava num dia que ainda não existia para a oficina. O
 *  mesmo deslocamento encurtava "Semana" e "Trimestre" em um dia. */
function getRange(preset: PeriodPreset): { from: string; to: string } {
  const to = todayIsoIn();

  if (preset === 'week') {
    return { from: addDaysIsoDate(to, -7), to };
  }
  if (preset === 'quarter') {
    const [y, m, d] = to.split('-').map(Number);
    // Dia 31 três meses atrás pode não existir (31/05 → 31/02). O
    // `Date.UTC` normaliza para o mês seguinte, então prendemos ao último
    // dia do mês alvo — "trimestre" nunca deve pular para frente.
    const target = new Date(Date.UTC(y, m - 1 - 3, 1));
    const lastDay = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
    target.setUTCDate(Math.min(d, lastDay));
    return { from: target.toISOString().slice(0, 10), to };
  }
  // month (default) — do dia 1 do mês corrente até hoje
  return { from: `${to.slice(0, 7)}-01`, to };
}

interface TeamMember {
  id: string;
  name: string;
}

const STATUS_OPTIONS = [
  { value: '', label: 'Todos os status' },
  { value: 'in_analysis', label: 'Em Análise' },
  { value: 'in_execution', label: 'Em Execução' },
  { value: 'waiting_part', label: 'Aguardando Peça' },
  { value: 'outsourced', label: 'Terceirizado' },
  { value: 'completed', label: 'Concluída' },
  { value: 'waiting_payment', label: 'Ag. Pagamento' },
  { value: 'cancelled', label: 'Cancelada' },
];

function ReportExportDropdown({
  from,
  to,
  mechanicId,
  status,
}: {
  from: string;
  to: string;
  mechanicId?: string;
  status?: string;
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState<'csv' | 'pdf' | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const { toast } = useToast();

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleExport = useCallback(
    async (format: 'csv' | 'pdf') => {
      setLoading(format);
      setOpen(false);
      try {
        const params: Record<string, string> = { format, from, to };
        if (mechanicId) params.mechanicId = mechanicId;
        if (status) params.status = status;
        const res = await api.get('/workshops/my/service-orders/reports/export', {
          params,
          responseType: 'blob',
        });
        const blob = new Blob([res.data], {
          type: format === 'csv' ? 'text/csv' : 'application/pdf',
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `mustauto-relatorio-os-${from}-${to}.${format}`;
        a.click();
        URL.revokeObjectURL(url);
      } catch (err) {
        // F-UI Phase 5: surface export failure (Sentry still receives it).
        console.error('[Reports] export failed', err);
        toast({
          type: 'error',
          title: 'Falha ao exportar relatório',
          description: formatApiError(err, 'Não foi possível gerar o arquivo. Verifique o intervalo e tente novamente.'),
        });
      } finally {
        setLoading(null);
      }
    },
    [from, to, mechanicId, status, toast],
  );

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        disabled={loading !== null}
        className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
      >
        {loading ? (
          <Loader2 size={16} className="animate-spin" />
        ) : (
          <Download size={16} />
        )}
        Exportar
      </button>
      {open && (
        <div className="absolute right-0 top-full z-10 mt-1 w-48 rounded-lg border border-border bg-card shadow-lg">
          <button
            onClick={() => handleExport('csv')}
            className="flex w-full items-center gap-2 px-4 py-2.5 text-sm hover:bg-muted"
          >
            <FileSpreadsheet size={16} />
            Exportar CSV
          </button>
          <button
            onClick={() => handleExport('pdf')}
            className="flex w-full items-center gap-2 px-4 py-2.5 text-sm hover:bg-muted"
          >
            <FileText size={16} />
            Exportar PDF
          </button>
        </div>
      )}
    </div>
  );
}

export function ReportsPage() {
  const { user } = useAuth();
  const [preset, setPreset] = useState<PeriodPreset>('month');
  const [mechanicId, setMechanicId] = useState('');
  const [status, setStatus] = useState('');

  const range = useMemo(() => getRange(preset), [preset]);

  const { data, isLoading, isError, error, refetch } = useOsReports({
    from: range.from,
    to: range.to,
    mechanicId: mechanicId || undefined,
    status: status || undefined,
  });

  const teamQuery = useQuery<{ team: TeamMember[] }>({
    queryKey: ['team-list'],
    queryFn: async () => {
      const res = await api.get('/workshops/my/team');
      return res.data;
    },
  });

  if (!user?.workshopId) {
    return (
    <EmptyState
      icon={<Building2 size={28} />}
      title="Sua conta não está vinculada a uma oficina"
      description="Isso costuma ser sessão desatualizada: recarregue a página. Se continuar, o suporte resolve o vínculo."
      action={{ label: 'Recarregar', onClick: () => window.location.reload() }}
      secondaryAction={{ label: 'Falar com o suporte', href: '/support' }}
    />
    );
  }

  // O erro vem ANTES do carregando: com `isLoading || !data` como único gate,
  // a busca que falha deixa `isLoading` em false e `data` em undefined — a
  // condição segue verdadeira e a tela fica girando para sempre, sem frase,
  // sem botão e sem saída (Regra 11).
  if (isError && !data) {
    return <ErrorState title="Não foi possível carregar os relatórios" error={error} onRetry={() => refetch()} />;
  }

  if (isLoading || !data) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  const { summary, byMechanic, byCategory, timeline } = data;

  const chartFormatted = timeline.map((d) => ({
    ...d,
    date: formatDayMonthBR(d.date),
  }));

  return (
    <div className="space-y-6">
      {/* Header + filters */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold">Relatórios</h1>
          <p className="text-sm text-muted-foreground">
            {range.from} a {range.to}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex gap-2">
            {(['week', 'month', 'quarter'] as const).map((p) => (
              <button
                key={p}
                onClick={() => setPreset(p)}
                className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                  preset === p
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-card text-foreground hover:bg-muted'
                }`}
              >
                {p === 'week' ? 'Semana' : p === 'month' ? 'Mês' : 'Trimestre'}
              </button>
            ))}
          </div>
          <select
            className="rounded-lg border border-border bg-card px-3 py-2 text-sm"
            value={mechanicId}
            onChange={(e) => setMechanicId(e.target.value)}
          >
            <option value="">Todos os mecânicos</option>
            {teamQuery.data?.team.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
          <select
            className="rounded-lg border border-border bg-card px-3 py-2 text-sm"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <ReportExportDropdown
            from={range.from}
            to={range.to}
            mechanicId={mechanicId || undefined}
            status={status || undefined}
          />
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KPICard
          title="Total de OS"
          value={String(summary.total)}
          icon={<ClipboardList size={20} />}
          subtitle="no período"
        />
        <KPICard
          title="Concluídas"
          value={String(summary.completed)}
          icon={<CheckCircle2 size={20} />}
          subtitle={
            summary.total > 0
              ? `${Math.round((summary.completed / summary.total) * 100)}% do total`
              : '0% do total'
          }
        />
        <KPICard
          title="Ticket Médio"
          value={formatReais(summary.avgTicket)}
          icon={<DollarSign size={20} />}
          subtitle="OS concluídas"
        />
        <KPICard
          title="Taxa Retrabalho"
          value={`${summary.reworkRate.toFixed(1)}%`}
          icon={<AlertTriangle size={20} />}
          subtitle="OS paradas há 3+ dias"
        />
      </div>

      {/* Timeline chart */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-semibold text-foreground">
            OS Abertas vs Concluídas
          </CardTitle>
        </CardHeader>
        <CardContent>
          {chartFormatted.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartFormatted}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="date" tick={{ fontSize: 12, fill: 'var(--color-muted-foreground)' }} stroke="var(--color-border)" />
                <YAxis tick={{ fontSize: 12, fill: 'var(--color-muted-foreground)' }} stroke="var(--color-border)" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'var(--color-card)',
                    borderColor: 'var(--color-border)',
                    borderRadius: '0.5rem',
                    color: 'var(--color-foreground)',
                  }}
                />
                <Bar dataKey="opened" name="Abertas" fill={CHART_COLORS.in_analysis} maxBarSize={48} />
                <Bar dataKey="completed" name="Concluídas" fill={CHART_COLORS.completed} maxBarSize={48} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <EmptyState
              compact
              icon={<BarChart3 size={22} />}
              title="Sem dados no período"
              description="Nenhuma OS foi aberta ou concluída entre as datas escolhidas."
            />
          )}
        </CardContent>
      </Card>

      {/* By Mechanic table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-semibold text-foreground">
            Por Mecânico
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {byMechanic.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Mecânico</TableHead>
                  <TableHead>Total OS</TableHead>
                  <TableHead>Concluídas</TableHead>
                  <TableHead>Ticket Médio</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {byMechanic.map((m) => (
                  <TableRow key={m.mechanicId}>
                    <TableCell className="font-medium">{m.mechanicName}</TableCell>
                    <TableCell>{m.total}</TableCell>
                    <TableCell>{m.completed}</TableCell>
                    <TableCell>{formatReais(m.avgTicket)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <EmptyState
              compact
              icon={<Users size={22} />}
              title="Nenhum dado por mecânico"
              description="A tabela preenche quando houver OS concluída no período."
            />
          )}
        </CardContent>
      </Card>

      {/* By Category table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-semibold text-foreground">
            Por Categoria
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {byCategory.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Categoria</TableHead>
                  <TableHead>Qtd OS</TableHead>
                  <TableHead>Faturamento</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {byCategory.map((c) => (
                  <TableRow key={c.category}>
                    <TableCell className="font-medium">{c.category}</TableCell>
                    <TableCell>{c.count}</TableCell>
                    <TableCell>{formatReais(c.revenue)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <EmptyState
              compact
              icon={<Tags size={22} />}
              title="Nenhum dado por categoria"
              description="A tabela preenche quando houver OS concluída no período."
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
